// src/pages/AdminPanel.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc, deleteDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { createNotification, getCustomers, getErrorLogs, deleteErrorLog, deleteAllErrorLogs } from '../services/firestoreService';
import { listJobsForAdmin, deleteJobsAndLinkedData } from '../services/adminJobCleanup';
import { useAuth } from '../context/AuthContext';
import BrandName from '../components/BrandName';
import AdminJobsTable from '../components/AdminJobsTable';
import { EA_CARRIER_REGISTER, NRW_CARRIER_REGISTER } from '../data/site';
import './AdminPanel.css';

const AdminPanel = () => {
  useEffect(() => { document.title = 'Admin — The WasteGame'; }, []);
  const { user } = useAuth();
  const [isAdmin, setIsAdmin]     = useState(null); // null = checking
  const [carriers, setCarriers]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [updating, setUpdating]   = useState('');

  const [customers,            setCustomers]            = useState([]);
  const [loadingCustomers,     setLoadingCustomers]     = useState(false);
  const [customersError,       setCustomersError]       = useState('');

  const [deletionRequests,     setDeletionRequests]     = useState([]);
  const [loadingDeletions,     setLoadingDeletions]     = useState(false);
  const [updatingDeletion,     setUpdatingDeletion]     = useState('');

  const [activeAdminTab,       setActiveAdminTab]       = useState('carriers');
  const [errorLogs,            setErrorLogs]            = useState([]);
  const [loadingLogs,          setLoadingLogs]          = useState(false);
  const [logsError,            setLogsError]            = useState('');
  const [expandedStacks,       setExpandedStacks]       = useState({});
  const [expandedMessages,     setExpandedMessages]     = useState({});

  const [jobs,                 setJobs]                 = useState([]);
  const [loadingJobs,          setLoadingJobs]          = useState(false);
  const [jobsError,            setJobsError]            = useState('');
  const [deletingJobs,         setDeletingJobs]         = useState(false);

  // Check admin role on mount / user change
  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (!cancelled) {
          setIsAdmin(snap.exists() && snap.data().role === 'admin');
        }
      } catch {
        if (!cancelled) setIsAdmin(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Fetch carriers, customers, deletion requests, and error logs once admin is confirmed
  useEffect(() => {
    if (isAdmin) {
      fetchCarriers();
      fetchCustomers();
      fetchDeletionRequests();
      fetchErrorLogs();
      fetchJobs();
    }
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCarriers = async () => {
    setLoading(true);
    setFetchError('');
    try {
      const q = query(collection(db, 'users'), where('type', '==', 'provider'));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCarriers(data);
    } catch (err) {
      setFetchError(`${err.code ?? 'error'}: ${err.message}`);
    }
    setLoading(false);
  };

  const fetchCustomers = async () => {
    setLoadingCustomers(true);
    setCustomersError('');
    try {
      const data = await getCustomers();
      setCustomers(data);
    } catch (err) {
      setCustomersError(`${err.code ?? 'error'}: ${err.message}`);
    }
    setLoadingCustomers(false);
  };

  const setVerified = async (carrierId, value) => {
    setUpdating(carrierId);
    try {
      await updateDoc(doc(db, 'users', carrierId), { verified: value });
      setCarriers(prev =>
        prev.map(c => c.id === carrierId ? { ...c, verified: value } : c)
      );
      // Notify the carrier — wrapped in try/catch so a failure never blocks the verify action
      try {
        if (value === true) {
          await createNotification(
            carrierId,
            'carrier_verified',
            'Your waste carrier registration has been checked against the public register and approved. You can now bid on jobs.'
          );
          console.log(`[AdminPanel] carrier_verified notification sent to ${carrierId}`);
        } else {
          await createNotification(
            carrierId,
            'carrier_unverified',
            'Your waste carrier registration has been revoked. Contact info@thewastegame.co.uk for details.'
          );
          console.log(`[AdminPanel] carrier_unverified notification sent to ${carrierId}`);
        }
      } catch (notifErr) {
        console.error('[AdminPanel] notification failed (verify action still succeeded):', notifErr.message);
      }
    } catch (err) {
      alert(`Could not update carrier: ${err.message}`);
    }
    setUpdating('');
  };

  const setEarlyAdopter = async (carrierId, value) => {
    setUpdating(carrierId + '_ea');
    try {
      await updateDoc(doc(db, 'users', carrierId), { earlyAdopter: value });
      setCarriers(prev =>
        prev.map(c => c.id === carrierId ? { ...c, earlyAdopter: value } : c)
      );
    } catch (err) {
      alert(`Could not update carrier: ${err.message}`);
    }
    setUpdating('');
  };

  const fetchDeletionRequests = async () => {
    setLoadingDeletions(true);
    try {
      const q = query(collection(db, 'deletionRequests'), where('status', '==', 'pending'));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => {
        const aT = a.requestedAt?.toDate?.() ?? new Date(0);
        const bT = b.requestedAt?.toDate?.() ?? new Date(0);
        return bT - aT;
      });
      setDeletionRequests(data);
    } catch (err) {
      console.error('[AdminPanel] fetchDeletionRequests:', err);
    }
    setLoadingDeletions(false);
  };

  const loadJobs = async () => {
    const rows = await listJobsForAdmin();
    setJobs(rows);
    return rows;
  };

  const fetchJobs = async () => {
    setLoadingJobs(true);
    setJobsError('');
    try {
      await loadJobs();
    } catch (err) {
      setJobsError(`${err.code ?? 'error'}: ${err.message}`);
    }
    setLoadingJobs(false);
  };

  const handleDeleteJobs = async (jobIds) => {
    setDeletingJobs(true);
    try {
      await deleteJobsAndLinkedData(jobIds);
      setJobsError('');
      await loadJobs();
    } catch (err) {
      const message = err?.message || 'Could not delete the selected jobs.';
      try {
        await loadJobs();
      } catch (refreshErr) {
        console.error('[AdminPanel] job list refresh failed:', refreshErr);
      }
      setJobsError(message);
      throw err;
    } finally {
      setDeletingJobs(false);
    }
  };

  const fetchErrorLogs = async () => {
    setLoadingLogs(true);
    setLogsError('');
    try {
      const logs = await getErrorLogs(50);
      setErrorLogs(logs);
    } catch (err) {
      setLogsError(`${err.code ?? 'error'}: ${err.message}`);
    }
    setLoadingLogs(false);
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Delete this error log entry?')) return;
    try {
      await deleteErrorLog(logId);
      setErrorLogs(prev => prev.filter(l => l.id !== logId));
    } catch (err) {
      alert(`Failed to delete log: ${err.message}`);
    }
  };

  const handleDeleteAllLogs = async () => {
    if (!window.confirm(`Delete ALL error logs? This cannot be undone.`)) return;
    try {
      const count = await deleteAllErrorLogs();
      setErrorLogs([]);
      console.log(`[AdminPanel] deleted ${count} error log(s)`);
    } catch (err) {
      alert(`Failed to delete all logs: ${err.message}`);
    }
  };

  const toggleStack = (id) =>
    setExpandedStacks(prev => ({ ...prev, [id]: !prev[id] }));

  const toggleMessage = (id) =>
    setExpandedMessages(prev => ({ ...prev, [id]: !prev[id] }));

  const handleApproveDeletion = async (req) => {
    if (!window.confirm(
      `Approve deletion for ${req.displayName} (${req.email})?\n\nThis will:\n• Delete their profile photo from Storage\n• Delete their Firestore user doc\n• Mark this request as approved\n\nNote: their Firebase Auth account must be deleted manually from the Firebase Console.`
    )) return;

    setUpdatingDeletion(req.id);
    try {
      // 1. Delete profile photo from Storage (ignore if it doesn't exist)
      try {
        await deleteObject(storageRef(storage, `users/${req.uid}/profile-photo`));
      } catch { /* no photo — fine */ }

      // 2. Delete Firestore user doc
      await deleteDoc(doc(db, 'users', req.uid));

      // 3. Mark request as approved
      await updateDoc(doc(db, 'deletionRequests', req.id), {
        status:      'approved',
        processedAt: serverTimestamp(),
      });

      setDeletionRequests(prev => prev.filter(r => r.id !== req.id));
    } catch (err) {
      alert(`Failed to process deletion: ${err.message}`);
    }
    setUpdatingDeletion('');
  };

  const handleRejectDeletion = async (req) => {
    setUpdatingDeletion(req.id + '_reject');
    try {
      await updateDoc(doc(db, 'deletionRequests', req.id), {
        status:      'rejected',
        processedAt: serverTimestamp(),
      });
      setDeletionRequests(prev => prev.filter(r => r.id !== req.id));
    } catch (err) {
      alert(`Failed to reject request: ${err.message}`);
    }
    setUpdatingDeletion('');
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Europe/London',
    });
  };

  // Loading / access states
  if (isAdmin === null) {
    return (
      <div className="admin-page">
        <div className="admin-login-card">
          <div className="admin-logo"><BrandName /></div>
          <p className="text-muted" style={{ marginTop: '16px' }}>Checking access...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-page">
        <div className="admin-login-card">
          <div className="admin-logo"><BrandName /></div>
          <h2>Access Denied</h2>
          <p className="admin-sub">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-wrapper">
        <div className="admin-header">
          <h1 className="section-title">ADMIN <span>PANEL</span></h1>
          <p className="text-muted">Platform management</p>
        </div>

        {/* TAB NAV */}
        <div className="admin-tab-nav">
          <button
            className={`admin-tab-btn${activeAdminTab === 'carriers' ? ' active' : ''}`}
            onClick={() => setActiveAdminTab('carriers')}
          >
            Carriers
            {carriers.filter(c => !c.verified).length > 0 && (
              <span className="admin-tab-badge amber">{carriers.filter(c => !c.verified).length}</span>
            )}
          </button>
          <button
            className={`admin-tab-btn${activeAdminTab === 'customers' ? ' active' : ''}`}
            onClick={() => setActiveAdminTab('customers')}
          >
            Customers
            {customers.length > 0 && (
              <span className="admin-tab-badge teal">{customers.length}</span>
            )}
          </button>
          <button
            className={`admin-tab-btn${activeAdminTab === 'jobs' ? ' active' : ''}`}
            onClick={() => setActiveAdminTab('jobs')}
          >
            Jobs
            {jobs.length > 0 && (
              <span className="admin-tab-badge amber">{jobs.length}</span>
            )}
          </button>
          <button
            className={`admin-tab-btn${activeAdminTab === 'errorLogs' ? ' active' : ''}`}
            onClick={() => setActiveAdminTab('errorLogs')}
          >
            Error Logs
            {errorLogs.length > 0 && (
              <span className="admin-tab-badge fire">{errorLogs.length}</span>
            )}
          </button>
        </div>

        {/* ── CARRIERS TAB ── */}
        {activeAdminTab === 'carriers' && (<>
        <div className="admin-stats">
          <div className="astat">
            <div className="astat-val">{carriers.length}</div>
            <div className="astat-lbl">Total Carriers</div>
          </div>
          <div className="astat">
            <div className="astat-val teal">{carriers.filter(c => c.verified).length}</div>
            <div className="astat-lbl">Verified</div>
          </div>
          <div className="astat">
            <div className="astat-val amber">{carriers.filter(c => !c.verified).length}</div>
            <div className="astat-lbl">Pending Approval</div>
          </div>
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',marginBottom:'8px'}}>
          <button className="btn btn-sm admin-refresh" onClick={fetchCarriers}>↻ Refresh Carriers</button>
        </div>

        {loading && <p className="text-muted" style={{marginTop:'24px'}}>Loading carriers...</p>}

        {!loading && fetchError && (
          <div className="admin-fetch-error">
            <strong>⚠️ Fetch failed</strong>
            <code>{fetchError}</code>
            <button className="btn btn-sm admin-refresh" style={{marginTop:'10px'}} onClick={fetchCarriers}>
              ↻ Retry
            </button>
          </div>
        )}

        {!loading && !fetchError && carriers.length === 0 && (
          <div className="card" style={{textAlign:'center',padding:'40px',marginTop:'20px'}}>
            <p className="text-muted">No carriers registered yet.</p>
          </div>
        )}

        {!loading && carriers.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Carrier</th>
                  <th>Email</th>
                  <th>Licence number</th>
                  <th>Vehicle</th>
                  <th>Joined</th>
                  <th>Status</th>
                  <th>Early Adopter</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {carriers.map(c => (
                  <tr key={c.id} className={c.verified ? 'row-verified' : 'row-pending'}>
                    <td>
                      <div className="admin-carrier-name">{c.name || '—'}</div>
                    </td>
                    <td className="admin-email">{c.email || '—'}</td>
                    <td>
                      <div className="admin-licence-cell">
                        <span className="admin-licence">{c.licenceNo || '—'}</span>
                        {c.licenceNo ? (
                          <>
                            <a
                              className="admin-ea-link"
                              href={EA_CARRIER_REGISTER}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Registered in England? Environment Agency register
                            </a>
                            <a
                              className="admin-ea-link"
                              href={NRW_CARRIER_REGISTER}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Registered in Wales? Natural Resources Wales register
                            </a>
                          </>
                        ) : (
                          <span className="admin-no-licence">No licence on file</span>
                        )}
                      </div>
                    </td>
                    <td>{c.vehicle || '—'}</td>
                    <td>{formatDate(c.createdAt)}</td>
                    <td>
                      {c.verified
                        ? <span className="badge badge-teal">✅ Verified</span>
                        : <span className="badge badge-amber">⏳ Pending</span>
                      }
                    </td>
                    <td>
                      {c.earlyAdopter ? (
                        <button
                          className="btn btn-sm admin-btn-revoke"
                          disabled={updating === c.id + '_ea'}
                          onClick={() => setEarlyAdopter(c.id, false)}
                        >
                          {updating === c.id + '_ea' ? '...' : 'REMOVE 🎖️'}
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm admin-btn-approve"
                          disabled={updating === c.id + '_ea'}
                          onClick={() => setEarlyAdopter(c.id, true)}
                        >
                          {updating === c.id + '_ea' ? '...' : 'GRANT 🎖️'}
                        </button>
                      )}
                    </td>
                    <td>
                      {c.verified ? (
                        <button
                          className="btn btn-sm admin-btn-revoke"
                          disabled={updating === c.id}
                          onClick={() => setVerified(c.id, false)}
                        >
                          {updating === c.id ? '...' : 'REVOKE'}
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm admin-btn-approve"
                          disabled={updating === c.id}
                          onClick={() => setVerified(c.id, true)}
                        >
                          {updating === c.id ? '...' : 'APPROVE'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── DELETION REQUESTS ── */}
        <div className="admin-section-divider">
          <h2 className="section-title admin-section-heading">DELETION <span>REQUESTS</span></h2>
          <button className="btn btn-sm admin-refresh" onClick={fetchDeletionRequests}>↻ Refresh</button>
        </div>

        {loadingDeletions && (
          <p className="text-muted" style={{ marginTop: '16px' }}>Loading requests...</p>
        )}

        {!loadingDeletions && deletionRequests.length === 0 && (
          <div className="card admin-empty-card">
            <p className="text-muted">No pending deletion requests.</p>
          </div>
        )}

        {!loadingDeletions && deletionRequests.length > 0 && (
          <>
            <div className="admin-deletion-note">
              ⚠️ Approving a request deletes the user's Firestore profile and profile photo.
              Their <strong>Firebase Auth account must be deleted manually</strong> from the Firebase Console.
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Reason</th>
                    <th>Requested</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deletionRequests.map(req => (
                    <tr key={req.id} className="row-pending">
                      <td><div className="admin-carrier-name">{req.displayName || '—'}</div></td>
                      <td className="admin-email">{req.email || '—'}</td>
                      <td>
                        <span className={`badge ${req.role === 'provider' ? 'badge-teal' : 'badge-amber'}`}>
                          {req.role === 'provider' ? 'Carrier' : 'Customer'}
                        </span>
                      </td>
                      <td className="admin-deletion-reason">{req.reason || <em className="admin-no-licence">No reason given</em>}</td>
                      <td>{formatDate(req.requestedAt)}</td>
                      <td>
                        <div className="admin-deletion-actions">
                          <button
                            className="btn btn-sm admin-btn-approve"
                            disabled={updatingDeletion === req.id || updatingDeletion === req.id + '_reject'}
                            onClick={() => handleApproveDeletion(req)}
                          >
                            {updatingDeletion === req.id ? '...' : 'APPROVE'}
                          </button>
                          <button
                            className="btn btn-sm admin-btn-revoke"
                            disabled={updatingDeletion === req.id || updatingDeletion === req.id + '_reject'}
                            onClick={() => handleRejectDeletion(req)}
                          >
                            {updatingDeletion === req.id + '_reject' ? '...' : 'REJECT'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        </>)}
        {/* END CARRIERS TAB */}

        {/* ── CUSTOMERS TAB ── */}
        {activeAdminTab === 'customers' && (<>
        <div className="admin-stats">
          <div className="astat">
            <div className="astat-val">{customers.length}</div>
            <div className="astat-lbl">Total Customers</div>
          </div>
          <div className="astat">
            <div className="astat-val teal">
              {customers.filter(c => c.emailVerified === true).length}
            </div>
            <div className="astat-lbl">Email Verified</div>
          </div>
          <div className="astat">
            <div className="astat-val amber">
              {customers.filter(c => c.emailVerified !== true).length}
            </div>
            <div className="astat-lbl">Unverified</div>
          </div>
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',marginBottom:'8px'}}>
          <button className="btn btn-sm admin-refresh" onClick={fetchCustomers}>↻ Refresh Customers</button>
        </div>

        {loadingCustomers && <p className="text-muted" style={{marginTop:'24px'}}>Loading customers...</p>}

        {!loadingCustomers && customersError && (
          <div className="admin-fetch-error">
            <strong>⚠️ Fetch failed</strong>
            <code>{customersError}</code>
            <button className="btn btn-sm admin-refresh" style={{marginTop:'10px'}} onClick={fetchCustomers}>
              ↻ Retry
            </button>
          </div>
        )}

        {!loadingCustomers && !customersError && customers.length === 0 && (
          <div className="card" style={{textAlign:'center',padding:'40px',marginTop:'20px'}}>
            <p className="text-muted">No customers registered yet.</p>
          </div>
        )}

        {!loadingCustomers && customers.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Verified</th>
                  <th>Signed Up</th>
                  {customers.some(c => c.jobsPosted != null) && <th>Jobs Posted</th>}
                </tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c.id} className="row-verified">
                    <td>
                      <div className="admin-carrier-name">{c.name || '—'}</div>
                    </td>
                    <td className="admin-email">{c.email || '—'}</td>
                    <td>
                      {c.emailVerified === true
                        ? <span className="badge badge-teal">✅ Verified</span>
                        : <span className="badge badge-amber">❌ Unverified</span>
                      }
                    </td>
                    <td>{formatDate(c.createdAt)}</td>
                    {customers.some(cu => cu.jobsPosted != null) && (
                      <td>{c.jobsPosted != null ? c.jobsPosted : '—'}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>)}
        {/* END CUSTOMERS TAB */}

        {/* ── JOBS TAB ── */}
        {activeAdminTab === 'jobs' && (
          <AdminJobsTable
            jobs={jobs}
            loading={loadingJobs}
            error={jobsError}
            deleting={deletingJobs}
            onRefresh={fetchJobs}
            onDeleteJobs={handleDeleteJobs}
          />
        )}
        {/* END JOBS TAB */}

        {/* ── ERROR LOGS TAB ── */}
        {activeAdminTab === 'errorLogs' && (
          <div className="admin-errorlogs-section">
            <div className="admin-errorlogs-toolbar">
              <div className="admin-errorlogs-count">
                {loadingLogs ? 'Loading…' : `${errorLogs.length} log${errorLogs.length !== 1 ? 's' : ''} (latest 50)`}
              </div>
              <div style={{display:'flex',gap:'8px'}}>
                <button className="btn btn-sm admin-refresh" onClick={fetchErrorLogs} disabled={loadingLogs}>
                  ↻ Refresh
                </button>
                {errorLogs.length > 0 && (
                  <button className="btn btn-sm admin-btn-revoke" onClick={handleDeleteAllLogs}>
                    🗑 Delete All
                  </button>
                )}
              </div>
            </div>

            {logsError && (
              <div className="admin-fetch-error">
                <strong>⚠️ Failed to load logs</strong>
                <code>{logsError}</code>
              </div>
            )}

            {!loadingLogs && !logsError && errorLogs.length === 0 && (
              <div className="card admin-empty-card">
                <p className="text-muted">No errors logged — everything running smoothly ✓</p>
              </div>
            )}

            {errorLogs.map(log => {
              const msg   = log.message || log.error || '(no message)';
              const short = msg.length > 120 ? msg.slice(0, 120) + '…' : msg;
              const isExpMsg   = expandedMessages[log.id];
              const isExpStack = expandedStacks[log.id];

              return (
                <div key={log.id} className="admin-errorlog-card">
                  <div className="admin-errorlog-top">
                    <div className="admin-errorlog-meta">
                      <span className="admin-errorlog-ts">{formatDateTime(log.timestamp)}</span>
                      {log.type && <span className="admin-errorlog-type">{log.type}</span>}
                      <span className="admin-errorlog-uid">
                        {log.userId ? `uid: ${log.userId}` : 'anonymous'}
                      </span>
                    </div>
                    <button
                      className="btn btn-sm admin-btn-revoke"
                      style={{fontSize:'0.72rem',padding:'3px 10px'}}
                      onClick={() => handleDeleteLog(log.id)}
                    >
                      Delete
                    </button>
                  </div>

                  <div className="admin-errorlog-msg">
                    {isExpMsg ? msg : short}
                    {msg.length > 120 && (
                      <button className="admin-errorlog-toggle" onClick={() => toggleMessage(log.id)}>
                        {isExpMsg ? ' Show less' : ' Show more'}
                      </button>
                    )}
                  </div>

                  {log.url && (
                    <div className="admin-errorlog-url">{log.url}</div>
                  )}

                  {(log.stack || log.componentStack) && (
                    <div style={{marginTop:'8px'}}>
                      <button className="admin-errorlog-toggle" onClick={() => toggleStack(log.id)}>
                        {isExpStack ? '▾ Hide stack' : '▸ Show stack'}
                      </button>
                      {isExpStack && (
                        <pre className="admin-errorlog-stack">
                          {[log.stack, log.componentStack].filter(Boolean).join('\n\n--- Component Stack ---\n')}
                        </pre>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {/* END ERROR LOGS TAB */}

      </div>
    </div>
  );
};

export default AdminPanel;
